interface ValueStackSectionProps {
  onOpenForm: () => void;
}

const stackItems = [
  {
    label: 'Custom Website Build (up to 10 pages)',
    value: '$2,500+',
    icon: 'ri-layout-masonry-line',
    note: 'Agencies charge this upfront — before SEO even starts',
  },
  {
    label: 'SEO Setup & 12-Month Strategy',
    value: '$1,000+',
    icon: 'ri-search-2-line',
    note: 'Keyword research, technical SEO, monthly optimization',
  },
  {
    label: 'AI + Voice Search Optimization',
    value: '$1,500+',
    icon: 'ri-robot-2-line',
    note: 'Most agencies don\'t even offer this yet',
  },
  {
    label: 'Conversion Design & CRO',
    value: '$1,000+',
    icon: 'ri-focus-3-line',
    note: 'Psychology-driven layout built to convert visitors',
  },
];

export default function ValueStackSection({ onOpenForm }: ValueStackSectionProps) {
  return (
    <section className="bg-[#f8faf9] py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

          {/* Left — Value list */}
          <div>
            <p className="text-emerald-500 text-xs font-bold tracking-widest uppercase mb-4">Real Market Value</p>
            <h2 className="text-3xl md:text-5xl font-black text-[#0d1117] leading-tight mb-4">
              What This Would Cost You Elsewhere
            </h2>
            <p className="text-[#6b7280] text-base md:text-lg leading-relaxed mb-10">
              Hiring an agency for each piece separately? Here's what you'd pay — just to get started:
            </p>

            {/* Stack rows */}
            <div className="space-y-3 mb-5">
              {stackItems.map((item) => (
                <div
                  key={item.label}
                  className="bg-white border border-[#e9ecef] rounded-xl px-5 py-4 hover:border-emerald-200 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between gap-4 mb-1">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#f0fdf4] border border-emerald-100 flex-shrink-0">
                        <i className={`${item.icon} text-emerald-600`} style={{ fontSize: '16px' }}></i>
                      </div>
                      <span className="text-[#0d1117] text-sm font-semibold">{item.label}</span>
                    </div>
                    <span className="text-[#0d1117] font-black text-base whitespace-nowrap">{item.value}</span>
                  </div>
                  <p className="text-[#9ca3af] text-xs pl-12">{item.note}</p>
                </div>
              ))}
            </div>

            {/* Total strikethrough */}
            <div className="bg-[#f1f3f4] border border-[#d1d5db] rounded-xl px-5 py-4 flex items-center justify-between">
              <span className="text-[#6b7280] font-semibold text-sm">Total if purchased separately</span>
              <span className="text-[#9ca3af] font-black text-xl line-through">$6,000+</span>
            </div>
          </div>

          {/* Right — Price card */}
          <div className="lg:sticky lg:top-28">
            <div className="relative bg-[#0d1117] rounded-2xl border border-white/10 overflow-hidden">

              {/* Top accent bar */}
              <div className="h-1 bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-400"></div>

              <div className="px-8 md:px-10 pt-8 pb-8">
                {/* Urgency badge */}
                <div className="inline-flex items-center gap-2 bg-amber-500/12 border border-amber-500/25 rounded-full px-3.5 py-1.5 mb-6">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"></span>
                  <span className="text-amber-300 text-xs font-bold tracking-wide uppercase">Limited to 10 Clients/Month</span>
                </div>

                <p className="text-emerald-400 text-xs font-bold tracking-widest uppercase mb-3">Your Investment</p>

                {/* Price — visually dominant */}
                <div className="mb-2">
                  <div className="flex items-start gap-1.5">
                    <span className="text-white/50 text-xl mt-3">$</span>
                    <span className="text-8xl font-black bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent leading-none tracking-tighter">399</span>
                    <span className="text-white/40 text-lg mt-auto mb-2">/mo</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-8">
                  <span className="text-white/35 text-sm line-through">$6,000+ elsewhere</span>
                  <span className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">Save $5,600+</span>
                </div>

                {/* Value highlight box */}
                <div className="bg-emerald-500/12 border border-emerald-500/20 rounded-xl px-5 py-4 mb-7">
                  <p className="text-emerald-300 font-bold text-base mb-1">$0 Upfront — Website Included</p>
                  <p className="text-white/45 text-sm">You get a full website rebuild before paying a cent. No risk.</p>
                </div>

                {/* Terms checklist */}
                <div className="space-y-3 mb-8">
                  {[
                    { icon: 'ri-wallet-3-line', text: '$0 to start — website is part of the package', em: true },
                    { icon: 'ri-calendar-check-line', text: '90-day initial commitment', em: false },
                    { icon: 'ri-refresh-line', text: 'Month-to-month after — cancel anytime', em: false },
                    { icon: 'ri-shield-check-line', text: 'No long-term contracts, no setup fees', em: false },
                  ].map((t) => (
                    <div key={t.text} className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-500/15 flex-shrink-0">
                        <i className={`${t.icon} text-emerald-400`} style={{ fontSize: '14px' }}></i>
                      </div>
                      <span className={`text-sm ${t.em ? 'text-emerald-300 font-semibold' : 'text-white/55'}`}>{t.text}</span>
                    </div>
                  ))}
                </div>

                {/* CTA button */}
                <button
                  onClick={onOpenForm}
                  className="whitespace-nowrap w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-base py-4 rounded-xl hover:opacity-92 hover:scale-[1.01] transition-all duration-200 cursor-pointer mb-3 flex items-center justify-center gap-2.5"
                >
                  Claim This Offer — Get Free Preview
                  <i className="ri-arrow-right-line" style={{ fontSize: '18px' }}></i>
                </button>

                <a
                  href="tel:8004559670"
                  className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-white/6 border border-white/12 text-white/70 text-sm font-semibold py-3.5 rounded-xl hover:bg-white/10 transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-phone-fill text-emerald-400" style={{ fontSize: '15px' }}></i>
                  Or call 800-455-9670
                </a>

                <p className="text-white/25 text-xs text-center mt-3">No credit card required. Free preview first.</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
